import { defineStore } from 'pinia'
import { getBookings, getBookingDetails } from '../services/myBookingsService'
import { useUserStore } from './userStore'
import { BookingTariffSegment, RequiredProduct } from '../types/bookingTypes'

export interface Booking {
  booking_number: string
  segments: any[]
  meta_data?: {
    cancel_datetime?: string
  }
  [key: string]: any
}

export interface ServiceDetails {
  departure_station: {
    name: string
    departure_timestamp: string
    via_stations?: any[]
    _u_i_c_station_code?: string
  }
  arrival_station: {
    name: string
    arrival_timestamp: string
    _u_i_c_station_code?: string
  }
}

export interface BookingDetail {
  booking: any
  isLoading: boolean
  error: string | null
  lastUpdated: number
}

export interface MyBookingsState {
  futureBookings: Booking[]
  pastBookings: Booking[]
  serviceDetailsCache: Record<string, ServiceDetails>
  bookingDetailsCache: Record<string, BookingDetail>
  isLoading: boolean
  isLoadingMore: boolean
  isLoadingDetails: boolean
  errorMessage: string
  nextPage: string | null
  pastNextPage: string | null
  hasFetchedFutureBookings: boolean
  hasFetchedPastBookings: boolean
  detailsErrorMessage: string
}

export const useMyBookingsStore = defineStore('myBookings', {
  state: (): MyBookingsState => ({
    futureBookings: [],
    pastBookings: [],
    serviceDetailsCache: {},
    bookingDetailsCache: {},
    isLoading: false,
    isLoadingMore: false,
    isLoadingDetails: false,
    errorMessage: '',
    nextPage: null,
    pastNextPage: null,
    hasFetchedFutureBookings: false,
    hasFetchedPastBookings: false,
    detailsErrorMessage: ''
  }),

  getters: {
    getFutureBookings: (state) => state.futureBookings,
    getPastBookings: (state) => state.pastBookings,
    getIsLoading: (state) => state.isLoading,
    getIsLoadingMore: (state) => state.isLoadingMore,
    getIsLoadingDetails: (state) => state.isLoadingDetails,
    getErrorMessage: (state) => state.errorMessage,
    getDetailsErrorMessage: (state) => state.detailsErrorMessage,
    getHasFutureNextPage: (state) => !!state.nextPage,
    getHasPastNextPage: (state) => !!state.pastNextPage,
    getBookingDetails: (state) => (bookingIdentifier: string) => {
      return state.bookingDetailsCache[bookingIdentifier]?.booking || null
    },
    getBookingDetailsStatus: (state) => (bookingIdentifier: string) => {
      const details = state.bookingDetailsCache[bookingIdentifier]
      if (!details) {
        return { exists: false, isLoading: false, hasError: false, error: null }
      }
      return {
        exists: !!details.booking,
        isLoading: details.isLoading,
        hasError: !!details.error,
        error: details.error,
        lastUpdated: details.lastUpdated
      }
    },
    getAllBookingDetails: (state) => state.bookingDetailsCache
  },

  actions: {
    resetState() {
      this.futureBookings = []
      this.pastBookings = []
      this.isLoading = false
      this.isLoadingMore = false
      this.isLoadingDetails = false
      this.errorMessage = ''
      this.detailsErrorMessage = ''
      this.nextPage = null
      this.pastNextPage = null
      this.hasFetchedFutureBookings = false
      this.hasFetchedPastBookings = false
    },
    
    isProvisionalBooking(bookingNumber: string): boolean {
      try {
        const bookingDetails = this.getBookingDetails(bookingNumber)
        
        if (!bookingDetails || !bookingDetails.data || !bookingDetails.data.booking) {
          return false;
        }
        
        const booking = bookingDetails.data.booking;
        
        const hasProvisionalOutbound = booking.outbound_booking_tariff_segments?.some(
          (segment: BookingTariffSegment) => segment.required_products?.some((product: RequiredProduct) => product.provisional === true && !product.cancelled)
        ) || false;
        
        const hasProvisionalInbound = booking.inbound_booking_tariff_segments?.some(
          (segment: BookingTariffSegment) => segment.required_products?.some((product: RequiredProduct) => product.provisional === true && !product.cancelled)
        ) || false;
        
        const isProvisional = hasProvisionalOutbound || hasProvisionalInbound;
        return isProvisional;
      } catch (error) {
        console.error('Error checking if booking is provisional:', error);
        return false;
      }
    },

    findOriginalBooking(provisionalBookingNumber: string): string | null {
      try {
        const bookingDetails = this.getBookingDetails(provisionalBookingNumber)
        
        if (!bookingDetails?.data?.booking) {
          return null;
        }
        
        const booking = bookingDetails.data.booking;   
        const originalBookingNumber = booking.original_booking_number || booking.related_booking_number || null;
        
        if (originalBookingNumber) {
          return originalBookingNumber;
        }
        
        return originalBookingNumber;
      } catch (error) {
        console.error('Error finding original booking:', error);
        return null;
      }
    },

    setErrorMessage(message: string) {
      this.errorMessage = message
    },
    clearErrorMessage() {
      this.errorMessage = ''
      this.detailsErrorMessage = ''
    },

    getOutboundSegment(booking: Booking) {
      const outboundSegments = booking.segments?.filter(
        (segment: any) => segment.direction === 'OUTBOUND'
      )
      
      if (outboundSegments.length === 0) return null
      
      return outboundSegments[0]
    },

    async getTransferJourneyDetails(booking: Booking) {
      const outboundSegments =
        booking.segments?.filter(
          (segment: any) => segment.direction === 'OUTBOUND'
        ) || []
      if (outboundSegments.length === 0) return

      try {
        if (outboundSegments.length === 1) {
          const segment = outboundSegments[0]
          
          if (!segment.stationDetails) {
            segment.stationDetails = {
              departure_station: {
                name: segment.origin_station_name || segment.origin_station_code || '',
                departure_timestamp: segment.departure_date_time || segment.schedule_date
              },
              arrival_station: {
                name: segment.destination_station_name || segment.destination_station_code || '',
                arrival_timestamp: segment.arrival_date_time || ''
              }
            }
          }
          return
        }

        const firstSegment = outboundSegments[0]
        const lastSegment = outboundSegments[outboundSegments.length - 1]

        if (!firstSegment.stationDetails) {
          firstSegment.stationDetails = {
            departure_station: {
              name: firstSegment.origin_station_name || firstSegment.origin_station_code || '',
              departure_timestamp: firstSegment.departure_date_time || firstSegment.schedule_date
            },
            arrival_station: {
              name: firstSegment.destination_station_name || firstSegment.destination_station_code || '',
              arrival_timestamp: firstSegment.arrival_date_time || ''
            }
          }
        }

        if (!lastSegment.stationDetails) {
          lastSegment.stationDetails = {
            departure_station: {
              name: lastSegment.origin_station_name || lastSegment.origin_station_code || '',
              departure_timestamp: lastSegment.departure_date_time || lastSegment.schedule_date
            },
            arrival_station: {
              name: lastSegment.destination_station_name || lastSegment.destination_station_code || '',
              arrival_timestamp: lastSegment.arrival_date_time || ''
            }
          }
        }
      } catch (error) {
        console.error('Error setting up transfer journey details:', error)
      }
    },
    
    async loadFutureBookings(url?: string) {
      const userStore = useUserStore()
      
      if (!userStore.token || !userStore.user?.id || !userStore.user?.email) {
        return
      }

      if (!url && this.isLoading) {
        return
      }

      if (url) {
        this.isLoadingMore = true
      } else {
        this.isLoading = true
      }
      this.errorMessage = ''

      try {
        const now = new Date()
        const today = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate()
        )
        const todayFormatted = today.toISOString().split('T')[0]
        
        const oneYearFromNow = new Date(
          now.getFullYear() + 1,
          now.getMonth(),
          now.getDate()
        )
        const futureEndDate = oneYearFromNow.toISOString().split('T')[0]        
        
        const response = await getBookings({
          token: userStore.token,
          userId: userStore.user.id.toString(),
          userEmail: userStore.user.email,
          includeCancelledBookings: false,
          includeCancelledSegments: true,
          sort: 'schedule_date',
          fromPage: url ? undefined : 0,
          pageSize: 50,
          travelStartDate: todayFormatted,
          travelEndDate: futureEndDate,
          url: url
        })

        if (response.bookings && response.bookings.length > 0) {
          await Promise.allSettled(
            response.bookings.map(async (booking: Booking) => {
              try {
                const existingDetails = this.bookingDetailsCache[booking.booking_number]
                if (!existingDetails?.booking) {
                  await this.fetchBookingDetails(booking.booking_number)
                }
              } catch (error) {
                console.error(`Error fetching details for booking ${booking.booking_number}:`, error)
              }
            })
          )
        }
        
        const provisionalBookings = new Map<string, string>();
        const originalBookings = new Set<string>();
        
        (response.bookings || []).forEach((booking: Booking) => {
          if (this.isProvisionalBooking(booking.booking_number)) {
            const originalBookingNumber = this.findOriginalBooking(booking.booking_number);
            if (originalBookingNumber) {
              provisionalBookings.set(booking.booking_number, originalBookingNumber);
              originalBookings.add(originalBookingNumber);
            }
          }
        });
        
        const filteredBookings = (response.bookings || []).filter(
          (booking: Booking) => {
            if (booking.meta_data?.cancel_datetime && originalBookings.has(booking.booking_number)) {
              return true;
            }
            
            if (booking.meta_data?.cancel_datetime) {
              return false;
            }
            
            if (this.isProvisionalBooking(booking.booking_number)) {
              return false;
            }
            
            const outboundSegment = this.getOutboundSegment(booking);
            if (!outboundSegment?.schedule_date) {
              return false;
            }
            
            const scheduleDate = new Date(outboundSegment.schedule_date);
            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            
            if (scheduleDate < today) {
              return false;
            }
            
            return true;
          }
        )
        
        filteredBookings.sort((a: Booking, b: Booking) => {
          const aSegment = this.getOutboundSegment(a);
          const bSegment = this.getOutboundSegment(b);
          
          if (!aSegment?.schedule_date) return 1;
          if (!bSegment?.schedule_date) return -1;
          
          const aDate = new Date(aSegment.schedule_date).getTime();
          const bDate = new Date(bSegment.schedule_date).getTime();
          
          return aDate - bDate;
        });
        
        if (filteredBookings.length > 0) {
          await Promise.allSettled(
            filteredBookings.map(async (booking: Booking) => {
              try {
                await this.getTransferJourneyDetails(booking)
              } catch (error) {
                console.error(`Error getting transfer journey details for booking ${booking.booking_number}:`, error)
              }
            })
          )
        }

        if (url) {
          this.futureBookings = [...this.futureBookings, ...filteredBookings]
        } else {
          this.futureBookings = filteredBookings
        }

        this.nextPage = response.nextPage || null
        this.hasFetchedFutureBookings = true
      } catch (error: any) {
        console.error('Error fetching future bookings:', error)
        this.errorMessage = error.message || 'Failed to fetch bookings'
      } finally {
        this.isLoading = false
        this.isLoadingMore = false
      }
    },
    
    // Load past bookings
    async loadPastBookings(url?: string) {
      const userStore = useUserStore()
      
      if (!userStore.token || !userStore.user?.id || !userStore.user?.email) {
        return
      }

      if (!url && this.isLoading) {
        return
      }

      if (url) {
        this.isLoadingMore = true
      } else {
        this.isLoading = true
      }
      this.errorMessage = ''

      try {
        const now = new Date()
        const today = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate()
        )
        const todayFormatted = today.toISOString().split('T')[0]
        
        const oneYearAgo = new Date(
          now.getFullYear() - 1,
          now.getMonth(),
          now.getDate()
        )
        const pastStartDate = oneYearAgo.toISOString().split('T')[0]        
        const response = await getBookings({
          token: userStore.token,
          userId: userStore.user.id.toString(),
          userEmail: userStore.user.email,
          includeCancelledBookings: true,
          includeCancelledSegments: true,
          sort: '-schedule_date',
          fromPage: url ? undefined : 0,
          pageSize: 50,
          travelStartDate: pastStartDate,
          travelEndDate: todayFormatted,
          url: url
        })

        if (response.bookings && response.bookings.length > 0) {
          await Promise.allSettled(
            response.bookings.map(async (booking: Booking) => {
              try {
                const existingDetails = this.bookingDetailsCache[booking.booking_number]
                if (!existingDetails?.booking) {
                  await this.fetchBookingDetails(booking.booking_number)
                }
              } catch (error) {
                console.error(`Error fetching details for past booking ${booking.booking_number}:`, error)
              }
            })
          )
        }
        
        const provisionalBookings = new Map<string, string>();
        const originalBookings = new Set<string>();
        
        (response.bookings || []).forEach((booking: Booking) => {
          if (this.isProvisionalBooking(booking.booking_number)) {
            const originalBookingNumber = this.findOriginalBooking(booking.booking_number);
            if (originalBookingNumber) {
              provisionalBookings.set(booking.booking_number, originalBookingNumber);
              originalBookings.add(originalBookingNumber);
            }
          }
        });
        
        const filteredBookings = (response.bookings || []).filter(
          (booking: Booking) => {
            if (booking.meta_data?.cancel_datetime) {
              const outboundSegment = this.getOutboundSegment(booking);
              if (!outboundSegment?.schedule_date) {
                return false;
              }
              
              if (this.isProvisionalBooking(booking.booking_number)) {
                return false;
              }              
              return true;
            }
            
            if (this.isProvisionalBooking(booking.booking_number)) {
              return false;
            }
            
            const outboundSegment = this.getOutboundSegment(booking);
            if (!outboundSegment?.schedule_date) {
              return false;
            }
            
            const scheduleDate = new Date(outboundSegment.schedule_date);
            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            
            if (scheduleDate >= today) {
              return false;
            }
            return true;
          }
        )
        
        filteredBookings.sort((a: Booking, b: Booking) => {
          const aSegment = this.getOutboundSegment(a);
          const bSegment = this.getOutboundSegment(b);
          
          if (!aSegment?.schedule_date) return 1;
          if (!bSegment?.schedule_date) return -1;
          
          const aDate = new Date(aSegment.schedule_date).getTime();
          const bDate = new Date(bSegment.schedule_date).getTime();
          
          return bDate - aDate;
        });

        if (filteredBookings.length > 0) {
          await Promise.allSettled(
            filteredBookings.map(async (booking: Booking) => {
              try {
                await this.getTransferJourneyDetails(booking)
              } catch (error) {
                console.error(`Error getting transfer journey details for booking ${booking.booking_number}:`, error)
              }
            })
          )
        }

        if (url) {
          this.pastBookings = [...this.pastBookings, ...filteredBookings]
        } else {
          this.pastBookings = filteredBookings
        }

        this.pastNextPage = response.nextPage || null
        this.hasFetchedPastBookings = true
      } catch (error: any) {
        console.error('Error fetching past bookings:', error)
        this.errorMessage = error.message || 'Failed to fetch bookings'
      } finally {
        this.isLoading = false
        this.isLoadingMore = false
      }
    },

    async fetchBookingDetails(bookingIdentifier: string, forceRefresh = false) {
      const userStore = useUserStore()
      
      if (!userStore.token || !userStore.user?.id || !userStore.user?.email) {
        return null
      }
      
      const existingDetails = this.bookingDetailsCache[bookingIdentifier]
      const now = Date.now()
      const cacheMaxAge = 5 * 60 * 1000 
      
      if (
        !forceRefresh &&
        existingDetails?.booking &&
        existingDetails.lastUpdated &&
        now - existingDetails.lastUpdated < cacheMaxAge
      ) {
        return existingDetails.booking
      }
      
      this.bookingDetailsCache = {
        ...this.bookingDetailsCache,
        [bookingIdentifier]: {
          booking: existingDetails?.booking || null,
          isLoading: true,
          error: null,
          lastUpdated: existingDetails?.lastUpdated || Date.now()
        }
      }
      
      this.isLoadingDetails = true
      this.detailsErrorMessage = ''
      
      try {
        const response = await getBookingDetails({
          token: userStore.token,
          bookingIdentifier
        })
        
        this.bookingDetailsCache = {
          ...this.bookingDetailsCache,
          [bookingIdentifier]: {
            booking: response.data?.booking || response,
            isLoading: false,
            error: null,
            lastUpdated: Date.now()
          }
        }
        
        this.isLoadingDetails = false
        return response.data?.booking || response
      } catch (error: any) {
        console.error(`Error fetching details for booking ${bookingIdentifier}:`, error)
        
        this.bookingDetailsCache = {
          ...this.bookingDetailsCache,
          [bookingIdentifier]: {
            booking: existingDetails?.booking || null,
            isLoading: false,
            error: error.message || 'Failed to fetch booking details',
            lastUpdated: existingDetails?.lastUpdated || Date.now()
          }
        }
        
        this.isLoadingDetails = false
        this.detailsErrorMessage = error.message || 'Failed to fetch booking details'
        return null
      }
    },
    
    async fetchMultipleBookingDetails(bookingIdentifiers: string[], forceRefresh = false) {
      if (!bookingIdentifiers || bookingIdentifiers.length === 0) {
        return []
      }

      const results = await Promise.allSettled(
        bookingIdentifiers.map(id => this.fetchBookingDetails(id, forceRefresh))
      )
      
      return results
        .filter((result): result is PromiseFulfilledResult<any> => result.status === 'fulfilled')
        .map(result => result.value)
    }
  }
})